// ---------------------------------------------------------------------------
// Map prop GLB model manifest — maps each prop type to its 3D model
// configuration. When GLB files are placed in /models/props/, the prop system
// will load them and replace the procedural fallback mesh.
// ---------------------------------------------------------------------------

export interface MapPropModelConfig {
  /** Path to GLB file, e.g. '/models/props/crate.glb' */
  glbPath: string;
  /** Uniform scale factor applied to the loaded model. */
  scale: number;
  /** Euler [x, y, z] rotation offset in radians. */
  rotationOffset: [number, number, number];
  /** Collision AABB size [w, h, d] from props.ts. null = no collision. */
  colliderSize: [number, number, number] | null;
  /** Whether the prop casts shadows. */
  castShadow: boolean;
  /** Whether the prop receives shadows. */
  receiveShadow: boolean;
  /**
   * Loading priority:
   * - 'blocking': visible from spawn (crate, barrel, car, barrier, sandbags)
   * - 'eager': secondary props loaded during freeze time
   * - 'lazy': decorative/rare props
   */
  priority: 'blocking' | 'eager' | 'lazy';
  /**
   * Optional: name of shared PBR texture set from /textures/{name}/.
   * If specified, the loader will apply these textures to the GLB model's
   * materials after loading, replacing any baked-in material colors.
   * Allows multiple props to share the same texture files (VRAM savings).
   * Available sets: 'wood-crates', 'wood-planks', 'metal-clean', 'metal-rusty',
   * 'concrete-clean', 'concrete-weathered', 'concrete-stained', 'brick-desert',
   * 'brick-dark', 'sand-dirt', 'asphalt', 'corrugated-metal', 'plaster', 'tiles'
   */
  textureSet?: string;
}

/**
 * Map prop model configurations. Each prop type maps to its GLB path,
 * transform offsets, and collision size (mirrored from props.ts).
 * Initially all entries exist but GLB files may not be present yet —
 * the prop system gracefully falls back to procedural meshes.
 */
export const MAP_PROP_MODELS: Record<string, MapPropModelConfig> = {
  // ── Blocking (visible from spawn — loaded at init) ──────────────────────

  crate: {
    glbPath: '/models/props/crate.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [1.2, 1.2, 1.2],
    castShadow: true,
    receiveShadow: true,
    priority: 'blocking',
    textureSet: 'wood-crates',
  },
  barrel: {
    glbPath: '/models/props/barrel.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.8, 1.0, 0.8],
    castShadow: true,
    receiveShadow: true,
    priority: 'blocking',
    textureSet: 'metal-clean',
  },
  car: {
    glbPath: '/models/props/car.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [4.0, 2.0, 1.8],
    castShadow: true,
    receiveShadow: true,
    priority: 'blocking',
    textureSet: 'metal-clean',
  },
  barrier: {
    glbPath: '/models/props/barrier.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.8, 0.8, 1.5],
    castShadow: true,
    receiveShadow: true,
    priority: 'blocking',
    textureSet: 'concrete-clean',
  },
  sandbags: {
    glbPath: '/models/props/sandbags.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [2.5, 0.75, 0.4],
    castShadow: true,
    receiveShadow: true,
    priority: 'blocking',
    textureSet: 'sand-dirt',
  },

  // ── Eager (secondary props — loaded during freeze time) ─────────────────

  crate_small: {
    glbPath: '/models/props/crate_small.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.7, 0.5, 0.7],
    castShadow: true,
    receiveShadow: true,
    priority: 'eager',
    textureSet: 'wood-crates',
  },
  barrel_rusty: {
    glbPath: '/models/props/barrel_rusty.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.8, 1.0, 0.8],
    castShadow: true,
    receiveShadow: true,
    priority: 'eager',
    textureSet: 'metal-rusty',
  },
  dumpster: {
    glbPath: '/models/props/dumpster.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [2.0, 1.4, 1.2],
    castShadow: true,
    receiveShadow: true,
    priority: 'eager',
    textureSet: 'metal-rusty',
  },
  ammo_crate: {
    glbPath: '/models/props/ammo_crate.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.8, 0.5, 0.6],
    castShadow: true,
    receiveShadow: true,
    priority: 'eager',
    textureSet: 'metal-clean',
  },
  chain_fence: {
    glbPath: '/models/props/chain_fence.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [2.0, 2.0, 0.05],
    castShadow: true,
    receiveShadow: false,
    priority: 'eager',
    textureSet: 'metal-clean',
  },
  scaffolding: {
    glbPath: '/models/props/scaffolding.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [2.0, 3.0, 1.5],
    castShadow: true,
    receiveShadow: true,
    priority: 'eager',
    textureSet: 'metal-clean',
  },
  sign: {
    glbPath: '/models/props/sign.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.1, 2.5, 0.1],
    castShadow: true,
    receiveShadow: false,
    priority: 'eager',
    textureSet: 'metal-clean',
  },
  bench: {
    glbPath: '/models/props/bench.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [1.8, 0.8, 0.5],
    castShadow: true,
    receiveShadow: true,
    priority: 'eager',
    textureSet: 'wood-planks',
  },
  folding_table: {
    glbPath: '/models/props/folding_table.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [1.5, 0.75, 0.8],
    castShadow: true,
    receiveShadow: true,
    priority: 'eager',
    textureSet: 'wood-planks',
  },
  ladder: {
    glbPath: '/models/props/ladder.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.5, 3.0, 0.1],
    castShadow: true,
    receiveShadow: false,
    priority: 'eager',
  },
  tires: {
    glbPath: '/models/props/tires.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.8, 0.5, 0.8],
    castShadow: true,
    receiveShadow: false,
    priority: 'eager',
  },
  pallet: {
    glbPath: '/models/props/pallet.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [1.2, 0.15, 0.8],
    castShadow: true,
    receiveShadow: true,
    priority: 'eager',
    textureSet: 'wood-crates',
  },

  // ── Lazy (decorative/rare props — loaded on demand) ─────────────────────

  light_fixture: {
    glbPath: '/models/props/light_fixture.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: false,
    receiveShadow: false,
    priority: 'lazy',
  },
  ac_unit: {
    glbPath: '/models/props/ac_unit.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.8, 0.5, 0.4],
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
  },
  pipe: {
    glbPath: '/models/props/pipe.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: true,
    receiveShadow: false,
    priority: 'lazy',
  },
  trash_bag: {
    glbPath: '/models/props/trash_bag.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.5, 0.6, 0.5],
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
  },
  fire_hydrant: {
    glbPath: '/models/props/fire_hydrant.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.3, 0.7, 0.3],
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
    textureSet: 'metal-rusty',
  },
  electrical_box: {
    glbPath: '/models/props/electrical_box.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.6, 0.8, 0.2],
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
  },
  cable_spool: {
    glbPath: '/models/props/cable_spool.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.8, 0.6, 0.8],
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
  },
  gas_tank: {
    glbPath: '/models/props/gas_tank.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.4, 1.0, 0.4],
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
    textureSet: 'metal-clean',
  },
  traffic_cone: {
    glbPath: '/models/props/traffic_cone.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.3, 0.7, 0.3],
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
  },
  cardboard_box: {
    glbPath: '/models/props/cardboard_box.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.5, 0.4, 0.5],
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
  },
  toolbox: {
    glbPath: '/models/props/toolbox.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.5, 0.25, 0.3],
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
  },

  // ── Phase 3: Architectural Detail Props ───────────────────────────────────

  door_wooden: {
    glbPath: '/models/props/door_wooden.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [1.0, 2.0, 0.1],
    castShadow: true,
    receiveShadow: true,
    priority: 'eager',
    textureSet: 'wood-planks',
  },
  door_metal: {
    glbPath: '/models/props/door_metal.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [1.0, 2.0, 0.1],
    castShadow: true,
    receiveShadow: true,
    priority: 'eager',
    textureSet: 'metal-clean',
  },
  window_frame: {
    glbPath: '/models/props/window_frame.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: true,
    receiveShadow: false,
    priority: 'lazy',
  },
  railing_metal: {
    glbPath: '/models/props/railing_metal.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [2.0, 1.0, 0.05],
    castShadow: true,
    receiveShadow: false,
    priority: 'eager',
    textureSet: 'metal-clean',
  },
  vent_wall: {
    glbPath: '/models/props/vent_wall.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: true,
    receiveShadow: false,
    priority: 'lazy',
  },
  vent_floor: {
    glbPath: '/models/props/vent_floor.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: false,
    receiveShadow: true,
    priority: 'lazy',
  },
  gutter_vertical: {
    glbPath: '/models/props/gutter_vertical.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: true,
    receiveShadow: false,
    priority: 'lazy',
  },
  awning_fabric: {
    glbPath: '/models/props/awning_fabric.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: true,
    receiveShadow: true,
    priority: 'eager',
  },
  concrete_bollard: {
    glbPath: '/models/props/concrete_bollard.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.3, 0.8, 0.3],
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
    textureSet: 'concrete-clean',
  },
  drain_cover: {
    glbPath: '/models/props/drain_cover.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: false,
    receiveShadow: false,
    priority: 'lazy',
  },

  // ── Phase 3: Environmental Detail Props ───────────────────────────────────

  rubble_pile: {
    glbPath: '/models/props/rubble_pile.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [1.5, 0.5, 1.5],
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
    textureSet: 'concrete-weathered',
  },
  potted_plant: {
    glbPath: '/models/props/potted_plant.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.4, 0.9, 0.4],
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
  },
  dead_bush: {
    glbPath: '/models/props/dead_bush.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: true,
    receiveShadow: false,
    priority: 'lazy',
  },
  generator_unit: {
    glbPath: '/models/props/generator_unit.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [1.2, 0.8, 0.6],
    castShadow: true,
    receiveShadow: true,
    priority: 'eager',
  },
  satellite_dish: {
    glbPath: '/models/props/satellite_dish.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: true,
    receiveShadow: false,
    priority: 'lazy',
  },
  warning_sign: {
    glbPath: '/models/props/warning_sign.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: true,
    receiveShadow: false,
    priority: 'lazy',
  },
  standing_lamp: {
    glbPath: '/models/props/standing_lamp.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.15, 3.0, 0.15],
    castShadow: true,
    receiveShadow: false,
    priority: 'eager',
  },
  emergency_light: {
    glbPath: '/models/props/emergency_light.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: false,
    receiveShadow: false,
    priority: 'lazy',
  },
  rebar_pile: {
    glbPath: '/models/props/rebar_pile.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [1.0, 0.3, 0.5],
    castShadow: true,
    receiveShadow: false,
    priority: 'lazy',
    textureSet: 'metal-rusty',
  },
  wire_spool: {
    glbPath: '/models/props/wire_spool.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.3, 0.3, 0.2],
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
  },

  // ── Phase 4: Surface & Detail Props ─────────────────────────────────────

  // Modular Wall/Floor Panels
  wall_concrete_4m: {
    glbPath: '/models/props/wall_concrete_4m.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
    textureSet: 'concrete-clean',
  },
  wall_brick_4m: {
    glbPath: '/models/props/wall_brick_4m.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
    textureSet: 'brick-desert',
  },
  floor_tile_4m: {
    glbPath: '/models/props/floor_tile_4m.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: false,
    receiveShadow: true,
    priority: 'lazy',
    textureSet: 'tiles',
  },
  corner_concrete: {
    glbPath: '/models/props/corner_concrete.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
    textureSet: 'concrete-clean',
  },
  wall_damaged: {
    glbPath: '/models/props/wall_damaged.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
    textureSet: 'concrete-weathered',
  },
  floor_metal: {
    glbPath: '/models/props/floor_metal.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: false,
    receiveShadow: true,
    priority: 'lazy',
    textureSet: 'metal-clean',
  },

  // Surface Detail Meshes
  brick_protrusion: {
    glbPath: '/models/props/brick_protrusion.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: true,
    receiveShadow: false,
    priority: 'lazy',
    textureSet: 'brick-desert',
  },
  rebar_exposed: {
    glbPath: '/models/props/rebar_exposed.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: true,
    receiveShadow: false,
    priority: 'lazy',
    textureSet: 'metal-rusty',
  },
  corrugated_panel: {
    glbPath: '/models/props/corrugated_panel.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
    textureSet: 'corrugated-metal',
  },
  curb_stone: {
    glbPath: '/models/props/curb_stone.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [2.0, 0.15, 0.3],
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
    textureSet: 'concrete-stained',
  },
  manhole_cover: {
    glbPath: '/models/props/manhole_cover.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: false,
    receiveShadow: false,
    priority: 'lazy',
  },
  pillar_concrete: {
    glbPath: '/models/props/pillar_concrete.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [0.4, 3.0, 0.4],
    castShadow: true,
    receiveShadow: true,
    priority: 'eager',
    textureSet: 'concrete-clean',
  },

  // ── Phase 3 Missing Props ─────────────────────────────────────────────────

  door_garage: {
    glbPath: '/models/props/door_garage.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [3.0, 2.5, 0.08],
    castShadow: true,
    receiveShadow: true,
    priority: 'eager',
  },
  railing_concrete: {
    glbPath: '/models/props/railing_concrete.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: [2.0, 1.0, 0.15],
    castShadow: true,
    receiveShadow: true,
    priority: 'lazy',
    textureSet: 'concrete-clean',
  },
  weeds: {
    glbPath: '/models/props/weeds.glb',
    scale: 1.0,
    rotationOffset: [0, 0, 0],
    colliderSize: null,
    castShadow: true,
    receiveShadow: false,
    priority: 'lazy',
  },
};

/**
 * Get all prop type names that should be loaded at a given priority level.
 */
export function getMapPropsByPriority(priority: 'blocking' | 'eager' | 'lazy'): string[] {
  const result: string[] = [];
  for (const [type, cfg] of Object.entries(MAP_PROP_MODELS)) {
    if (cfg.priority === priority) {
      result.push(type);
    }
  }
  return result;
}
